import { useMutation, useQuery } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { gql } from "~/__generated__"
import { useViewer } from "~/auth/use-viewer"
import { useFormErrorHandling } from "~/common/error-handling"
import { dashboardPath, rootPath } from "~/common/paths"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { Form } from "~/ui/form"
import { TextField } from "~/ui/forms/fields/text-field"
import { FormGeneralErrors } from "~/ui/forms/form-general-errors"
import { Section } from "~/ui/section"
import { AvatarUpload } from "./avatar-upload"

const updateMutation = gql(/* GraphQL */ `
  mutation UpdateProfile($input: ViewerUpdateInput!) {
    viewerUpdate(input: $input) {
      user {
        id
        firstName
        lastName
        ...CachedViewerContext
      }
    }
  }
`)

const softDeleteMutation = gql(/* GraphQL */ `
  mutation ViewerSoftDelete($input: ViewerSoftDeleteInput!) {
    viewerSoftDelete(input: $input) {
      success
    }
  }
`)

const dataExportQuery = gql(/* GraphQL */ `
  query ViewerDataExport {
    viewerDataExport
  }
`)

export const ProfileEditScreen = () => {
  const { viewer } = useViewer()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const formSchema = z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.string(),
    zipCode: z.string().optional(),
    bio: z.string().max(2000).optional(),
    defaultDisplayAs: z.enum(["FIRST_NAME", "ANONYMOUS"]),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      firstName: viewer.firstName ?? "",
      lastName: viewer.lastName ?? "",
      email: viewer.email ?? "",
      zipCode: "",
      bio: "",
      defaultDisplayAs: viewer.defaultDisplayAs ?? "FIRST_NAME",
    },
  })

  const { onError } = useFormErrorHandling(form)
  const [exec] = useMutation(updateMutation, { onError: () => null })
  const [softDelete] = useMutation(softDeleteMutation)
  const { refetch: fetchDataExport } = useQuery(dataExportQuery, { skip: true })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const result = await exec({
      onError,
      variables: {
        input: {
          viewerInput: {
            firstName: values.firstName,
            lastName: values.lastName,
            zipCode: values.zipCode?.trim() || null,
            bio: values.bio?.trim() || null,
            defaultDisplayAs: values.defaultDisplayAs,
          },
        },
      },
    })

    if (result.error) return
    toast.success("Profile updated")
  }

  const handleDownloadData = async () => {
    const { data, error } = await fetchDataExport()
    if (error || !data) {
      toast.error("Could not export your data")
      return
    }
    const blob = new Blob([data.viewerDataExport], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `wandering-puzzles-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.success("Downloaded your data")
  }

  const handleDelete = async () => {
    const { data, error } = await softDelete({ variables: { input: {} } })
    if (error || !data?.viewerSoftDelete.success) {
      toast.error("Could not delete account")
      return
    }
    toast.success("Account deleted")
    window.location.href = rootPath({})
  }

  return (
    <TablePageLayout>
      <Link to={dashboardPath({})} className="text-sm text-gray-500 hover:text-gray-700">
        ← Back to dashboard
      </Link>
      <div className="flex flex-1 flex-col">
        <div className="bottom-1 mb-4 flex items-center justify-between border-b py-4">
          <h1 className="text-2xl">Edit profile</h1>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormGeneralErrors control={form.control} className="mb-4" />

            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-8">
                <Section variant="grayBackground" className="space-y-4 p-0">
                  <TextField
                    control={form.control}
                    name="firstName"
                    label="First name (shown publicly)"
                    required
                  />
                  <TextField
                    control={form.control}
                    name="lastName"
                    label="Last name (private)"
                    required
                  />
                  <TextField control={form.control} name="email" label="Email address" disabled />
                  <TextField control={form.control} name="zipCode" label="Zip code (private)" />
                  <TextField control={form.control} name="bio" label="Short bio" />
                  <div className="space-y-1">
                    <label className="block text-sm text-gray-400">
                      Default display on new logs
                    </label>
                    <div className="flex gap-4 text-sm">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          value="FIRST_NAME"
                          {...form.register("defaultDisplayAs")}
                        />
                        Show my first name
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          value="ANONYMOUS"
                          {...form.register("defaultDisplayAs")}
                        />
                        Always anonymous
                      </label>
                    </div>
                  </div>
                </Section>
              </div>
              <div>
                <div className="rounded border p-8 pr-12">
                  <AvatarUpload />
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4 border-t pt-10">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Save
              </Button>
              <Link to={dashboardPath({})} className="text-sm text-neutral-400">
                Cancel
              </Link>
            </div>
          </form>
        </Form>

        <div className="mt-12 space-y-6 border-t pt-8">
          <h2 className="text-lg font-medium">Privacy</h2>
          <div>
            <Button type="button" variant="outline" onClick={handleDownloadData}>
              Download my data
            </Button>
            <p className="mt-2 max-w-prose text-sm text-gray-500">
              JSON export of your profile, registered puzzles, and journey logs.
            </p>
          </div>
          <div>
            {confirmDelete ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  This will hide your name from all logs and end your session. Old logs remain on
                  the puzzles&apos; pages, attributed to &ldquo;Anonymous&rdquo;.
                </p>
                <div className="flex gap-3">
                  <Button type="button" variant="destructive" onClick={handleDelete}>
                    Yes, delete my account
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}>
                Delete my account
              </Button>
            )}
          </div>
        </div>
      </div>
    </TablePageLayout>
  )
}
